/**
 * Nepal phone helpers.
 *
 * Kept out of the field component so the component file exports only a
 * component — React Fast Refresh cannot hot-reload a module that mixes the
 * two, and the dev-server experience degrades to a full reload on every edit.
 */

/** NEPSE accounts are domestic, so the country is fixed rather than chosen. */
export const NP_DIAL_CODE = '+977'

/** Ten local digits, prefixed. Callers store this form, never the raw input. */
export const toE164 = (local: string) => `${NP_DIAL_CODE}${local.replace(/\D/g, '')}`

/** Local Nepal mobile numbers are exactly this many digits. */
export const PHONE_DIGITS = 10

export const PHONE_MESSAGE = 'Enter your 10-digit mobile number.'

export const NEPAL_MOBILE_MESSAGE = 'Enter your 10-digit mobile number, starting with 9'

/** Digits and the separators people type or paste between them, with an optional +. */
const PHONE_LIKE = /^\+?[\d\s\-.()]*$/

/**
 * Whatever was typed or pasted, reduced to at most ten local digits.
 *
 * Separators go, and so does a leading 977 country code, but only when it
 * cannot be the start of a local number: a "+" says so outright, and more than
 * ten digits says so by length. Otherwise 977XXXXXXX is a real local number
 * and must not be clipped. Digits past the tenth are dropped, so a member
 * can never see an 11-digit number.
 */
export const toLocalDigits = (raw: string) => {
  let digits = raw.replace(/\D/g, '')
  const hasCountryCode =
    digits.startsWith('977') && (raw.trim().startsWith('+') || digits.length > PHONE_DIGITS)
  if (hasCountryCode) digits = digits.slice(3)
  return digits.slice(0, PHONE_DIGITS)
}

/** True for a value that is only digits and separators: a phone number, not an email. */
export const looksLikePhone = (raw: string) => PHONE_LIKE.test(raw.trim())

export const isTenDigitPhone = (local: string) =>
  local.length === PHONE_DIGITS && /^\d+$/.test(local)

/**
 * Sign-up only: NTC, Ncell and Smart Cell numbers all start with 9. Sign-in
 * and password reset use the looser ten-digit rule so no existing account is
 * ever locked out by it.
 */
export const isNepalMobile = (local: string) => isTenDigitPhone(local) && local.startsWith('9')

/**
 * The "Phone number or email" box as the member types.
 *
 * Emails and anything else pass through untouched; a phone number is
 * normalised as it goes in. A lone "+977" is held back from normalising so
 * a country code typed by hand is not eaten before the number follows it.
 */
export const toIdentifierInput = (raw: string) => {
  if (!looksLikePhone(raw)) return raw
  const t = raw.trim()
  if (t.startsWith('+') && '977'.startsWith(t.slice(1).replace(/\D/g, ''))) return t
  return toLocalDigits(raw)
}

/**
 * "98••••4567" / "s•••@example.com" — the contact a code went to, with the middle hidden (mock
 * `maskId`). A phone keeps its first two and last four digits, an email its first letter and its
 * domain, so a member can tell which detail it was without the screen reading it out.
 */
export const maskIdentifier = (value: string): string => {
  const at = value.indexOf('@')
  if (at > 0) return `${value.slice(0, 1)}•••${value.slice(at)}`
  const digits = value.replace(/\D/g, '')
  return digits.length <= 6 ? value : `${digits.slice(0, 2)}••••${digits.slice(-4)}`
}

/**
 * A stored number as the Account hub prints it (mock "+977 981-234-5521"): the dial code, then the
 * ten local digits in 3-3-4. Anything that is not a ten-digit local number is shown as stored.
 */
export const formatPhone = (raw: string): string => {
  const local = toLocalDigits(raw)
  return isTenDigitPhone(local)
    ? `${NP_DIAL_CODE} ${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`
    : raw
}
